import { useState } from 'react'
import { unsubscribe } from './preferencesApi'

/**
 * Отписка от писем-уведомлений по ссылке из письма (тикет #118).
 *
 * ## Вне приложения и без входа
 *
 * Страница монтируется в `main.tsx` мимо `AppProvider`: тот требует сессию, а читатель
 * письма может её не иметь — стена входа перед отпиской делает кнопку «спам» более лёгкой.
 * Тексты поэтому русские литералы, а не словарь: словарь и выбор языка живут в приложении,
 * а письмо, из которого сюда пришли, написано по-русски.
 *
 * ## Подтверждение, а не отписка при открытии
 *
 * Ссылки из писем открывают и сканеры почтовых шлюзов, и предпросмотр мессенджеров. Если бы
 * открытие страницы отписывало, человека отписывал бы антивирус его компании. Отписывает
 * нажатие (POST); почтовые клиенты с кнопкой «отписаться» шлют POST сами (RFC 8058).
 */

const KIND_NAMES: Record<string, string> = {
  mention: 'упоминания',
  task: 'задачи',
  approval: 'согласования',
  warning: 'предупреждения',
  info: 'сообщения системы',
  order: 'события заказов',
}

type State =
  | { step: 'ask' }
  | { step: 'busy' }
  | { step: 'done'; kind: string }
  | { step: 'failed'; status: number }

export function UnsubscribePage({ search = window.location.search }: { search?: string }) {
  const token = new URLSearchParams(search).get('token')
  const [state, setState] = useState<State>({ step: 'ask' })

  const confirm = () => {
    if (!token) return
    setState({ step: 'busy' })
    unsubscribe(token).then(
      (result) => setState(result.ok ? { step: 'done', kind: result.kind } : { step: 'failed', status: result.status }),
      () => setState({ step: 'failed', status: 0 }),
    )
  }

  return (
    <main className="stack" style={{ gap: 14, maxWidth: 520, margin: '64px auto', padding: 16 }}>
      <h1 className="h1">Письма-уведомления CRM</h1>
      {!token ? (
        <div className="alert alert-danger" role="alert">
          <span>В ссылке нет ключа отписки. Скопируйте её из письма целиком.</span>
        </div>
      ) : state.step === 'done' ? (
        <div className="alert alert-success" role="status">
          <span>
            Готово: письма про «{KIND_NAMES[state.kind] ?? state.kind}» больше не придут. В центре уведомлений
            они остаются; включить почту снова можно в его настройках.
          </span>
        </div>
      ) : state.step === 'failed' ? (
        <div className="alert alert-danger" role="alert">
          <span>
            {state.status === 404
              ? 'Ссылка недействительна: возможно, её скопировали не целиком. Отписаться можно и в настройках уведомлений в CRM.'
              : 'Сервер не ответил. Попробуйте ещё раз через минуту.'}
          </span>
          {state.status !== 404 && (
            <button className="btn btn-secondary btn-sm" type="button" onClick={confirm}>Повторить</button>
          )}
        </div>
      ) : (
        <>
          <p>Отписаться от писем этого вида? Уведомления в самой CRM продолжат приходить.</p>
          <div>
            <button className="btn btn-primary" type="button" disabled={state.step === 'busy'} onClick={confirm}>
              Отписаться
            </button>
          </div>
        </>
      )}
    </main>
  )
}
